import {
  buildCompanyBusinessLinks,
  buildCompanySocialLinks,
} from "@/lib/company-social-links";
import { employeeReviewFigures } from "@/lib/company-employee-reviews";
import { hasCompanyEventsData } from "@/lib/company-events";
import { latestFinancialRatios } from "@/lib/company-financial-ratios";
import { getGoogleMapsEmbedQuery } from "@/utils/google-maps";
import {
  CompanyDTO,
  displayableLegalForm,
  filterDirectors,
} from "@shaarp/common";

/*
 * Whether a card of the company page has something to show. Each card hides
 * itself on the same predicate, and `CompanySectionCard` reads it to choose
 * between the card, its skeleton while its sections run, and nothing.
 */

/** A phone number a person could dial, as the activity card lists it. */
export const isValidPhone = (phone: string): boolean =>
  /^\+?[\d\s\-().]{6,}$/.test(phone);

/** An email address, as the activity card lists it. */
export const isValidEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/** The activity card: what the company does, or a way to reach it. */
export function hasActivityData(company: CompanyDTO): boolean {
  const hasPhone = (company.contact.phones ?? []).some(
    (p) => !!p.phone && isValidPhone(p.phone),
  );
  const hasEmail = (company.contact.emails ?? []).some(
    (e) => !!e.email && isValidEmail(e.email),
  );
  return (
    !!company.activityMain ||
    !!company.websiteResume ||
    hasPhone ||
    hasEmail ||
    !!company.website ||
    buildCompanyBusinessLinks(company).length > 0 ||
    buildCompanySocialLinks(company).length > 0
  );
}

/** The key figures tiles of the insights tab: revenue or headcount, filed or estimated. */
export function hasKeyFiguresData(company: CompanyDTO): boolean {
  return (
    company.business.finances.length > 0 ||
    !!company.estimatedRevenue?.range ||
    company.employeesCount?.exact != null ||
    !!company.employeesCount?.estimatedRange
  );
}

const sumCounts = (counts: readonly (number | null)[]): number =>
  counts.reduce<number>((sum, count) => sum + Math.max(count ?? 0, 0), 0);

/**
 * The workforce breakdown card: a split of more than one person. Bars over a
 * single employee only repeat the employees table.
 */
export function hasWorkforceBreakdownData(company: CompanyDTO): boolean {
  const peopleCounted = Math.max(
    sumCounts(Object.values(company.departmentalHeadcount ?? {})),
    sumCounts(Object.values(company.seniorityBreakdown ?? {})),
    sumCounts(
      (company.employeesCount?.byCountry ?? [])
        .filter((item) => !!item.country)
        .map((item) => item.count),
    ),
  );
  return peopleCounted > 1;
}

/** The summary card: its directors and its registry facts. */
export function hasSummaryData(company: CompanyDTO): boolean {
  const { business, stock } = company;
  return (
    filterDirectors(business.executives).length > 0 ||
    !!business.creationDate ||
    !!displayableLegalForm(business.legalForm) ||
    !!business.enterpriseCategory ||
    (!!stock?.isPubliclyTraded && !!stock.ticker)
  );
}

/** How the market card reads a Coresignal ownership status, when it can. */
export function ownershipKind(
  rawStatus: string | null | undefined,
): "public" | "subsidiary" | null {
  const normalized = rawStatus?.toLowerCase() ?? "";
  if (normalized.includes("public")) return "public";
  if (normalized.includes("subsidiary")) return "subsidiary";
  return null;
}

/** The market card: B2B or not, categories, ownership. */
export function hasMarketData(company: CompanyDTO): boolean {
  return (
    company.isB2B != null ||
    (company.categories ?? []).length > 0 ||
    ownershipKind(company.business?.ownershipStatus) !== null
  );
}

/** The web traffic card: one measure of the site's audience. */
export function hasWebTrafficData(company: CompanyDTO): boolean {
  const traffic = company.webTraffic;
  if (!traffic) return false;
  return (
    (traffic.monthlyVisits ?? 0) > 0 ||
    (traffic.bounceRate ?? 0) > 0 ||
    (traffic.pagesPerVisit ?? 0) > 0 ||
    (traffic.avgVisitDurationSeconds ?? 0) > 0 ||
    traffic.rankGlobal != null ||
    traffic.rankCountry != null
  );
}

/** The technologies card: a technology or an ad pixel of the website. */
export function hasTechnologiesData(company: CompanyDTO): boolean {
  return (
    (company.technologies ?? []).length > 0 ||
    (company.adPixels ?? []).length > 0
  );
}

/** The financial ratios: one ratio of a filed year. */
export function hasFinancialRatiosData(company: CompanyDTO): boolean {
  return latestFinancialRatios(company.business?.finances) !== null;
}

/** The Google reviews card: Google's rating, or the themes of its reviews. */
export function hasGoogleReviewsData(company: CompanyDTO): boolean {
  return (
    company.reviews?.google?.rating != null ||
    hasGoogleReviewTopicsData(company)
  );
}

/** The Google reviews card without its rating: the themes of the reviews. */
export function hasGoogleReviewTopicsData(company: CompanyDTO): boolean {
  return (company.reviews?.google?.topics ?? []).length > 0;
}

/** The employee reviews card without its score: a sub-score, a share or a star count. */
export function hasEmployeeReviewsBreakdownData(company: CompanyDTO): boolean {
  if (!company.employeeReviews) return false;
  const figures = employeeReviewFigures(company.employeeReviews);
  return (
    figures.scores.length > 0 ||
    figures.shares.length > 0 ||
    figures.distribution.some(({ count }) => count > 0)
  );
}

/**
 * The employee reviews card with its score: the score, or a breakdown — as
 * the card renders them. A count alone shows nothing.
 */
export function hasEmployeeReviewsData(company: CompanyDTO): boolean {
  return (
    company.employeeReviews?.aggregateScore != null ||
    hasEmployeeReviewsBreakdownData(company)
  );
}

/** The map of the company and its opening hours. */
export function hasLocationData(company: CompanyDTO): boolean {
  return getGoogleMapsEmbedQuery(company) !== null;
}

/** The stock card: a listed company with its ticker, exchange or IPO date. */
export function hasStockData(company: CompanyDTO): boolean {
  const { stock } = company;
  return (
    !!stock?.isPubliclyTraded &&
    (!!stock.ticker || !!stock.exchange || !!stock.ipoDate)
  );
}

/**
 * The funding card: a metric of its fundraising, its rounds, or its
 * Crunchbase page — which alone does not open the Financials tab
 * ({@link hasFinancialData}).
 */
export function hasFundingData(company: CompanyDTO): boolean {
  return hasFundraisingData(company) || !!company.links?.crunchbase;
}

/** The funding card without its Crunchbase link: a metric or a round. */
function hasFundraisingData(company: CompanyDTO): boolean {
  const { funding, business } = company;
  return (
    (!!funding?.hasFundraised && hasAnyFundingMetric(funding)) ||
    (business?.fundingRounds ?? []).length > 0
  );
}

function hasAnyFundingMetric(funding: CompanyDTO["funding"]): boolean {
  return (
    (funding.totalFunding != null && funding.totalFunding > 0) ||
    !!funding.latestRoundType ||
    (funding.totalRoundsCount != null && funding.totalRoundsCount > 0) ||
    funding.investors.length > 0
  );
}

/** The financials summary: one measure of the last filed year. */
export function hasFinancialsSummaryData(company: CompanyDTO): boolean {
  const latest = company.business.finances[0];
  if (!latest) return false;
  return (
    latest.sales != null ||
    latest.result != null ||
    latest.ebitda != null ||
    latest.grossMargin != null
  );
}

/** The financials history: one filed year. */
export function hasFinancialsHistoryData(company: CompanyDTO): boolean {
  return (company.business.finances ?? []).length > 0;
}

/** A Pappers-like CompanyDTO["business"] with optional legal identity fields populated. */
export function hasLegalIdentityData(company: CompanyDTO): boolean {
  const business = company.business;
  return (
    !!(business.sirenFormatted || business.siren) ||
    !!(business.siretFormatted || business.siret) ||
    !!business.tvaIntra ||
    business.capital != null ||
    (!!business.nafCode && !!business.nafLabel) ||
    (business.collectiveAgreements?.length ?? 0) > 0 ||
    (business.corporateObject?.length ?? 0) > 0
  );
}

export function hasCertificationData(company: CompanyDTO): boolean {
  const c = company.certifications;
  const b = company.business;
  return (
    !!c?.isQualiopi ||
    !!c?.isRGE ||
    !!c?.isBio ||
    !!c?.isTrainingOrganization ||
    !!c?.isSIAE ||
    !!b?.isESSCompany ||
    !!b?.isMissionDriven
  );
}

/** Returns filtered aliases (excluding the primary domain). */
export function getFilteredDomainAliases(company: CompanyDTO): string[] {
  return (company.domainAliases ?? []).filter(
    (alias) => !!alias && alias !== company.domain,
  );
}

export function hasDomainAliasesData(company: CompanyDTO): boolean {
  return getFilteredDomainAliases(company).length > 0;
}

export function hasRelationshipsData(company: CompanyDTO): boolean {
  const rel = company.corporateRelationships;
  const b = company.business;
  return (
    (b.executives ?? []).length > 0 ||
    !!b.parentCompany ||
    (rel?.executiveMandates ?? []).length > 0 ||
    (rel?.holdingCompanies ?? []).length > 0 ||
    (rel?.acquisitions ?? []).length > 0 ||
    (rel?.affiliatedCompanies ?? []).length > 0 ||
    !!rel?.domiciliationCompany
  );
}

export function hasEstablishmentsData(company: CompanyDTO): boolean {
  return (company.establishments ?? []).length > 0;
}

/**
 * Returns true when the Financials tab has meaningful data to show. A
 * Crunchbase link alone is not, on purpose: the funding card shows it beside
 * other financials, but a tab holding only a link to another site says
 * nothing new about the company.
 */
export function hasFinancialData(company: CompanyDTO): boolean {
  return (
    hasFinancialsHistoryData(company) ||
    hasFundraisingData(company) ||
    hasStockData(company)
  );
}

/** Returns true when the News tab has meaningful data to show. */
export function hasNewsData(
  company: CompanyDTO,
  arePeopleVisible: boolean,
): boolean {
  const sn = company.socialNetworks;
  const hasSocialNetworkActivity =
    (sn?.linkedin?.followers ?? 0) > 0 ||
    (sn?.x?.followers ?? 0) > 0 ||
    (sn?.facebook?.likes ?? 0) > 0;
  const hasReviews = (company.reviews?.google?.review_count ?? 0) > 0;
  const hasJobOffers = (company.jobOffers?.count ?? 0) > 0;
  const hasPressArticles =
    (company.news?.count ?? 0) > 0 || (company.news?.articles ?? []).length > 0;
  const hasEmployeeReviews =
    !!company.employeeReviews &&
    ((company.employeeReviews.count ?? 0) > 0 ||
      company.employeeReviews.aggregateScore != null);
  return (
    hasSocialNetworkActivity ||
    hasReviews ||
    hasJobOffers ||
    hasPressArticles ||
    hasEmployeeReviews ||
    hasGoogleReviewsData(company) ||
    hasCompanyEventsData(company, arePeopleVisible)
  );
}

/** Returns true when the Others tab has meaningful data to show. */
export function hasOthersData(company: CompanyDTO): boolean {
  const hasCompetitors = company.competitors.length > 0;
  const tf = company.talentFlow;
  const hasTalentFlow =
    !!tf &&
    ((tf.topPreviousCompanies ?? []).length > 0 ||
      (tf.topNextCompanies ?? []).length > 0);
  return hasCompetitors || hasTalentFlow;
}
